'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useGlobalState } from '../context/GlobalStateContext';

// Consistent key for session storage, also used by other components
export const PROMO_BANNER_SESSION_KEY = 'promoBannerClosed';
export const PROMO_BANNER_HEIGHT_CLASS = 'h-14'; // 56px
export const PROMO_BANNER_NAVBAR_OFFSET_CLASS = 'top-14'; // For Navbar top style
export const PROMO_BANNER_CLOSED_EVENT = 'promoBannerClosedEvent'; // Custom event name

interface OnboardingState {
  isActive: boolean;
  userType?: 'patient' | 'provider';
  currentStep?: 'signup' | 'auth' | 'profile' | 'document' | 'complete';
  startedAt?: string;
}

// Storage key for onboarding state
const ONBOARDING_STORAGE_KEY = 'right2hope_onboarding';

// Default onboarding state
const defaultOnboardingState: OnboardingState = {
  isActive: false,
};

// Storage utilities
const saveOnboardingToStorage = (state: OnboardingState) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
      if (process.env.NODE_ENV === 'development') {
        console.log('useOnboarding: Saved onboarding to localStorage:', state);
      }
    } catch (error) {
      console.error('Failed to save onboarding to localStorage:', error);
    }
  }
};

const loadOnboardingFromStorage = (): OnboardingState => {
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (process.env.NODE_ENV === 'development') {
          console.log('useOnboarding: Loaded onboarding from localStorage:', parsed);
        }
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load onboarding from localStorage:', error);
    }
  }
  return defaultOnboardingState;
};

const clearOnboardingFromStorage = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      if (process.env.NODE_ENV === 'development') {
        console.log('useOnboarding: Cleared onboarding from localStorage');
      }
    } catch (error) {
      console.error('Failed to clear onboarding from localStorage:', error);
    }
  }
};

export const useOnboarding = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isProfileLoaded } = useGlobalState();

  // Onboarding state. Starts from the default so the server render and the first
  // client render agree; the stored state is restored on mount below.
  const [onboarding, setOnboarding] = useState<OnboardingState>(defaultOnboardingState);

  // Banner visibility state (only affects top banner)
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  // Determine user type based on current route context
  const isProviderContext = pathname?.includes('/provider');
  const userType = isProviderContext ? 'provider' : 'patient';

  // Restore persisted onboarding state. This must not run during render: this hook
  // is mounted by a server-rendered provider, and localStorage exists only in the
  // browser, so reading it in the initializer would make the first client render
  // disagree with the server HTML. Returns the shared default object when nothing
  // is stored, so React bails out of the re-render in that case.
  useEffect(() => {
    setOnboarding(loadOnboardingFromStorage());
  }, []);

  // Initialize banner visibility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedPreference = sessionStorage.getItem(PROMO_BANNER_SESSION_KEY);
      setIsBannerVisible(storedPreference !== 'true');
    }
  }, []);

  // Manage body class for banner visibility
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isBannerVisible && !user) {
        document.body.classList.add('promo-banner-visible');
      } else {
        document.body.classList.remove('promo-banner-visible');
      }
    }
  }, [isBannerVisible, user]);

  // Onboarding management functions
  const startOnboarding = useCallback(
    (targetUserType?: 'patient' | 'provider') => {
      const effectiveUserType = targetUserType || userType;
      const newState: OnboardingState = {
        isActive: true,
        userType: effectiveUserType,
        currentStep: 'signup',
        startedAt: new Date().toISOString(),
      };
      setOnboarding(newState);
      saveOnboardingToStorage(newState);

      // Navigate to auth page with signup parameter to ensure correct initial state
      const signupUrl =
        effectiveUserType === 'provider'
          ? '/users/auth/provider?onboarding=true'
          : '/users/auth/patient?onboarding=true';

      router.push(signupUrl);

      console.log('useOnboarding: Started onboarding for', effectiveUserType);
    },
    [router, userType],
  );

  const setOnboardingStep = useCallback((step: OnboardingState['currentStep']) => {
    setOnboarding(prev => {
      if (!prev.isActive) return prev;

      const newState: OnboardingState = {
        ...prev,
        currentStep: step,
      };
      saveOnboardingToStorage(newState);
      console.log('useOnboarding: Set onboarding step to', step);
      return newState;
    });
  }, []);

  const completeOnboarding = useCallback(() => {
    setOnboarding(prev => {
      const newState: OnboardingState = {
        ...prev,
        isActive: false,
        currentStep: 'complete',
      };
      saveOnboardingToStorage(newState);
      console.log('useOnboarding: Completed onboarding');
      return newState;
    });
  }, []);

  const clearOnboarding = useCallback(() => {
    setOnboarding(defaultOnboardingState);
    clearOnboardingFromStorage();
    console.log('useOnboarding: Cleared onboarding');
  }, []);

  const closeBanner = useCallback(() => {
    setIsBannerVisible(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(PROMO_BANNER_SESSION_KEY, 'true');
      window.dispatchEvent(new CustomEvent(PROMO_BANNER_CLOSED_EVENT));
      console.log('useOnboarding: Dispatched event:', PROMO_BANNER_CLOSED_EVENT);
    }
  }, []);

  // Separate trigger conditions for different use cases
  const authStatus = user ? 'authenticated' : 'unauthenticated';
  const isUnauthenticated = authStatus === 'unauthenticated';

  // Top banner: can be dismissed by user
  const shouldShowTopBanner = isUnauthenticated && isBannerVisible;

  // SearchBar upload promo: always shown for unauthenticated users (independent of banner dismissal)
  const shouldShowSearchBarUpload = isUnauthenticated;

  // Hero banner: always shown for unauthenticated users
  const shouldShowHeroBanner = isUnauthenticated;

  // Import banner: show for authenticated users who haven't completed onboarding
  const shouldShowImportBanner =
    authStatus === 'authenticated' && isProfileLoaded && onboarding.currentStep !== 'complete';

  return {
    // Onboarding state
    onboarding,
    startOnboarding,
    setOnboardingStep,
    completeOnboarding,
    clearOnboarding,

    // Banner visibility
    isBannerVisible,
    closeBanner,

    // Separate trigger conditions for different components
    shouldShowTopBanner,
    shouldShowSearchBarUpload,
    shouldShowHeroBanner,
    shouldShowImportBanner,

    // User context
    userType,
    isProviderContext,
    authStatus,
  };
};
